import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { isTenantAdmin } from "@/lib/tenants.functions";
import { isValidCoverImageUrl } from "@/lib/cover-image";
import { EVENT_TYPES, RSVP_MODES, isCrewOnlyEventType, type SquadEvent } from "@/lib/events";

/** Blank string, undefined and null all mean "no value" for optional fields. */
const emptyToNull = (val: unknown) =>
  val === null || val === undefined || (typeof val === "string" && val.trim() === "") ? null : val;

export const hostEventInputSchema = z.object({
  title: z.string().trim().min(2).max(120),
  slug: z
    .string()
    .trim()
    .min(2)
    .max(120)
    .regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and dashes only."),
  description: z.preprocess(emptyToNull, z.string().trim().max(2000).nullable().optional()),
  location: z.preprocess(emptyToNull, z.string().trim().max(255).nullable().optional()),
  starts_at: z.string().datetime(),
  ends_at: z.preprocess(emptyToNull, z.string().datetime().nullable().optional()),
  capacity: z.coerce.number().int().min(1).max(500).nullable().default(8),
  spectator_capacity: z.coerce.number().int().min(0).max(500).default(10),
  /** Community hosts can run the FREQ board for their own night. */
  freq_enabled: z.coerce.boolean().optional(),
  /** Training, Track Session, ... Crew-only types are rejected below. */
  event_type: z.enum(EVENT_TYPES).optional(),
  /** Hosts may run signups elsewhere; we then just link out. */
  rsvp_mode: z.enum(RSVP_MODES).optional(),
  multigp_event_url: z.preprocess(
    emptyToNull,
    z.string().trim().url().max(500).nullable().optional(),
  ),

  cover_image_url: z.preprocess(
    emptyToNull,
    z
      .string()
      .trim()
      .max(1000)
      .refine(isValidCoverImageUrl, "Enter an image link or upload a photo")
      .nullable()
      .optional(),
  ),

  // Admin-only: assign the event to another host pilot. Ignored for non-admins.
  host_pilot_id: z.preprocess(emptyToNull, z.string().uuid().nullable().optional()),
});

export const hostEventUpdateSchema = hostEventInputSchema.partial().extend({
  id: z.string().uuid(),
});

/** Friendly, recognisable failure when the database does not see our session. */
/** Taking a live event back to draft would strand anyone who already RSVP'd. */
export const UNPUBLISH_WITH_RSVPS_MESSAGE =
  "This event already has RSVPs. Cancel it instead of taking it back to draft.";

export function canUnpublish(rsvpCount: number | null, communityCount: number | null): boolean {
  return (rsvpCount ?? 0) + (communityCount ?? 0) === 0;
}

export const CREW_ONLY_TYPE_MESSAGE = "That event type is reserved for the SquadFlight crew.";

/** The UI hides crew-only types from hosts; this is the guard that enforces it. */
async function assertMaySetEventType(
  context: { supabase: any; userId?: string | null },
  eventType: unknown,
): Promise<void> {
  if (!eventType || !isCrewOnlyEventType(String(eventType))) return;
  if (await isTenantAdmin(context as never)) return;
  const { uid, tenantId } = await dbIdentity(context as never);
  const { data: crew } = await context.supabase.rpc("is_crew", {
    _user_id: uid,
    _tenant_id: tenantId,
  } as never);
  if (!crew) throw new Error(CREW_ONLY_TYPE_MESSAGE);
}

/**
 * Normalize a host's edit into a database patch: drop untouched fields, and
 * keep channel allocation in step with the host's FREQ switch.
 */
export function hostEventUpdatePatch(rawUpdates: Record<string, unknown>): Record<string, unknown> {
  const updates = Object.fromEntries(Object.entries(rawUpdates).filter(([, v]) => v !== undefined));
  if (updates["freq_enabled"] !== undefined) {
    updates["requires_freq_allocation"] = updates["freq_enabled"] !== false;
  }
  return updates;
}

export const SESSION_LOST_MESSAGE = "Your sign-in expired — sign in again to keep going.";

/**
 * Ask the database who it thinks is calling. Access rules on writes are evaluated
 * against this identity, so every pre-check runs against it too — the app and the
 * database can then never disagree about who you are.
 */
async function dbIdentity(context: { supabase: any; userId?: string | null }): Promise<{
  uid: string;
  tenantId: string;
  pilotId: string | null;
}> {
  if (!context.userId) throw new Error("Unauthorized");
  const { data, error } = await context.supabase.rpc("debug_auth_context" as never);
  const who = (data ?? {}) as {
    uid?: string | null;
    tenant?: string | null;
    pilot?: string | null;
  };
  if (error || !who.uid || who.uid !== context.userId) {
    console.error("db identity mismatch", { error, who, userId: context.userId });
    throw new Error(SESSION_LOST_MESSAGE);
  }
  if (!who.tenant) throw new Error("Could not resolve tenant.");
  return { uid: who.uid, tenantId: who.tenant, pilotId: who.pilot ?? null };
}

async function assertHost(context: { supabase: any; userId?: string | null }) {
  const { uid, tenantId } = await dbIdentity(context);
  const { data, error } = await context.supabase.rpc("is_host_pilot", {
    _user_id: uid,
    _tenant_id: tenantId,
  });
  if (error || !data) {
    console.error("is_host_pilot failed", error);
    throw new Error("Host access required.");
  }
}

// Ownership checks for co-host management now live in the database
// (add_event_cohost / remove_event_cohost), which also grant host access.

export async function assertHostsEvent(
  context: { supabase: any; userId?: string | null },
  eventId: string,
) {
  if (!context.userId) throw new Error("Unauthorized");
  const { data, error } = await context.supabase.rpc("hosts_event", {
    _event_id: eventId,
  });
  if (error || !data) {
    console.error("hosts_event failed", error);
    throw new Error("You do not have access to this event.");
  }
}

function actorEmail(claims: any): string | null {
  return claims?.email ?? null;
}

async function hostIdentity(
  supabase: any,
  pilotId: string,
): Promise<{ name: string; callsign: string | null; email: string | null }> {
  const { data } = await supabase
    .from("pilots")
    .select("first_name, last_name, callsign, email")
    .eq("id", pilotId)
    .maybeSingle();
  const name = [data?.first_name, data?.last_name].filter(Boolean).join(" ").trim();
  return {
    name: name || data?.callsign || "",
    callsign: data?.callsign ?? null,
    email: data?.email ?? null,
  };
}

async function pilotIdForUser(supabase: any, userId: string): Promise<string> {
  const { data, error } = await supabase.rpc("pilot_id_for_user", { _user_id: userId });
  if (error || !data) {
    console.error("pilot_id_for_user failed", error);
    throw new Error("Could not resolve your pilot record.");
  }
  return data;
}

/**
 * Diagnostic: what the database thinks of the caller on a server-function
 * request. Used when a host write is rejected by an access rule.
 */
export const myAuthContext = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.rpc("debug_auth_context" as never);
    return { userId: context.userId, db: data ?? null, error: error?.message ?? null };
  });

export const amIHost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: tenantId, error: tenantError } = await context.supabase.rpc("current_tenant_id");
    if (tenantError || !tenantId) {
      throw new Error("Could not resolve tenant.");
    }
    const { data, error } = await context.supabase.rpc("is_host_pilot", {
      _user_id: context.userId,
      _tenant_id: tenantId,
    });
    if (error) {
      console.error("amIHost failed", error);
      throw new Error("Could not verify host status.");
    }
    return { host: !!data };
  });

export const getMyHostEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertHost(context);
    const pilotId = await pilotIdForUser(context.supabase, context.userId!);

    const { data: owned, error: ownedError } = await context.supabase
      .from("events")
      .select(
        "id, title, slug, description, location, starts_at, ends_at, cover_image_url, status, campaign_id, partiful_url, multigp_event_url, rsvp_mode, featured, capacity, spectator_capacity, host_kind, host_pilot_id, freq_enabled, event_type, series_id, series_occurrence_date, detached_from_series, created_at, updated_at",
      )
      .eq("host_kind", "community")
      .eq("host_pilot_id", pilotId)
      .order("starts_at", { ascending: true });

    if (ownedError) {
      console.error("getMyHostEvents failed", ownedError);
      throw new Error("Could not load your events.");
    }

    const { data: cohosted, error: cohostError } = await context.supabase
      .from("event_cohosts")
      .select("event_id")
      .eq("pilot_id", pilotId);

    if (cohostError) {
      console.error("getMyHostEvents cohost lookup failed", cohostError);
      throw new Error("Could not load your events.");
    }

    const cohostEventIds = new Set((cohosted ?? []).map((c) => c.event_id));
    const events = (owned ?? [])
      .filter((row) => !cohostEventIds.has(row.id))
      .concat(
        cohostEventIds.size > 0
          ? ((
              await context.supabase
                .from("events")
                .select(
                  "id, title, slug, description, location, starts_at, ends_at, cover_image_url, status, campaign_id, partiful_url, multigp_event_url, rsvp_mode, featured, capacity, spectator_capacity, host_kind, host_pilot_id, freq_enabled, event_type, series_id, series_occurrence_date, detached_from_series, created_at, updated_at",
                )
                .eq("host_kind", "community")
                .in("id", Array.from(cohostEventIds))
            ).data ?? [])
          : [],
      )
      .map((row) => ({
        ...row,
        host_kind: (row.host_kind as "squadflight" | "community") ?? "community",
      })) as SquadEvent[];

    // Per-event RSVP tallies so the dashboard can answer "who's coming?"
    // without a click. Two grouped reads, no extra endpoint.
    const counts: Record<string, { going: number; waiting: number }> = {};
    const ids = events.map((e) => e.id);
    if (ids.length > 0) {
      const bump = (eventId: string, status: string, guests: number) => {
        const c = (counts[eventId] ??= { going: 0, waiting: 0 });
        if (status === "going") c.going += 1 + guests;
        else if (status === "waitlisted") c.waiting += 1;
      };

      const { data: pilotRsvps } = await context.supabase
        .from("event_rsvps")
        .select("event_id, status, guest_count")
        .in("event_id", ids)
        .neq("status", "cancelled");
      for (const r of (pilotRsvps ?? []) as any[]) {
        bump(r.event_id, r.status, Number(r.guest_count ?? 0));
      }

      const { data: communityRsvps } = await context.supabase
        .from("community_rsvps")
        .select("event_id, status, guest_count")
        .in("event_id", ids)
        .neq("status", "cancelled");
      for (const r of (communityRsvps ?? []) as any[]) {
        bump(r.event_id, r.status, Number(r.guest_count ?? 0));
      }
    }

    return { events, counts };
  });

export const createHostEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => hostEventInputSchema.parse(data))
  .handler(async ({ context, data }) => {
    // One identity read, evaluated exactly the way the write rule will be.
    const { uid, tenantId, pilotId: identityPilotId } = await dbIdentity(context);
    await assertMaySetEventType(context, (data as { event_type?: string }).event_type);
    const { data: isHost, error: hostError } = await context.supabase.rpc("is_host_pilot", {
      _user_id: uid,
      _tenant_id: tenantId,
    });
    if (hostError || !isHost) {
      console.error("is_host_pilot failed", hostError);
      throw new Error("Host access required.");
    }
    const actingPilotId = identityPilotId ?? (await pilotIdForUser(context.supabase, uid));

    const { host_pilot_id: requestedHostPilotId, ...rest } = data as typeof data & {
      host_pilot_id?: string | null;
    };

    // The host is always the owner. Only an admin may assign the event to another host.
    let pilotId = actingPilotId;
    if (requestedHostPilotId && requestedHostPilotId !== actingPilotId) {
      if (!(await isTenantAdmin({ supabase: context.supabase, userId: uid }))) {
        throw new Error("Only an admin can assign an event to another host.");
      }
      pilotId = requestedHostPilotId;
    }

    const cleanData = Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== undefined),
    ) as Record<string, unknown>;

    const baseSlug = String(cleanData["slug"] ?? "");
    let event: any = null;
    let error: any = null;

    // A taken link is not the host's problem: quietly try the next number.
    for (let attempt = 1; attempt <= 6; attempt++) {
      const slug = attempt === 1 ? baseSlug : `${baseSlug}-${attempt}`;
      const result = await context.supabase
        .from("events")
        .insert({
          tenant_id: tenantId,
          ...cleanData,
          slug,
          host_kind: "community",
          host_pilot_id: pilotId,
          created_by_user_id: uid,
          status: "draft",
          featured: false,
          // FREQ management and channel allocation are the same switch to a host.
          requires_freq_allocation: cleanData["freq_enabled"] !== false,
          visibility: "public",
          freq_escalation_hours: 24,
        } as never)
        .select()
        .single();
      event = result.data;
      error = result.error;
      if (!error) break;
      if (error.code !== "23505") break;
    }

    if (error || !event) {
      // Log how the database saw the caller on the failing request.
      const { data: who } = await context.supabase.rpc("debug_auth_context" as never);
      console.error("createHostEvent failed", error, "auth context:", who);
      if (error?.code === "23505") {
        throw new Error("That event link is already taken — try a different one.");
      }
      if (error?.code === "42501") {
        throw new Error(SESSION_LOST_MESSAGE);
      }
      throw new Error("Could not create event.");
    }

    return { event };
  });

/** Notify admins that a host event went live. Never blocks the host. */
async function notifyAdminsOfHostEvent(
  context: { supabase: any; claims: any },
  event: { id: string; title: string; slug: string; location: string | null; starts_at: string },
  hostPilotId: string,
  actingPilotId: string,
) {
  try {
    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    const { getAdminEmails } = await import("@/lib/admin.functions");
    const host = await hostIdentity(context.supabase, hostPilotId);
    const createdByEmail = actorEmail(context.claims as never) ?? "";
    const { data: cohostRows } = await context.supabase.rpc("event_cohosts_for_host", {
      _event_id: event.id,
    });
    const cohosts = ((cohostRows ?? []) as Array<{ label: string; callsign: string | null }>)
      .map((c) => (c.callsign ? `${c.label} (${c.callsign})` : c.label))
      .filter(Boolean)
      .join(", ");
    const admins = await getAdminEmails();
    for (const email of admins.emails) {
      await sendTemplateEmail("host-event-created", email, {
        templateData: {
          hostName: host.name,
          hostCallsign: host.callsign ?? "",
          hostEmail: host.email ?? "",
          createdByEmail: hostPilotId === actingPilotId ? "" : createdByEmail,
          cohosts,
          eventTitle: event.title,
          eventSlug: event.slug,
          eventLocation: event.location ?? "",
          eventStartsAt: event.starts_at,
        },
        idempotencyKey: `host-event-created-${event.id}-${email}`,
      });
    }
  } catch (e) {
    console.error("admin alert for host event failed", e);
  }
}

export const publishHostEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.id);

    const { data: event, error } = await context.supabase
      .from("events")
      .update({ status: "planned" } as never)
      .eq("id", data.id)
      .eq("status", "draft")
      .select()
      .single();

    if (error || !event) {
      console.error("publishHostEvent failed", error);
      throw new Error("Could not publish event.");
    }

    const actingPilotId = await pilotIdForUser(context.supabase, context.userId!);
    await notifyAdminsOfHostEvent(
      context as never,
      event as never,
      (event as { host_pilot_id: string }).host_pilot_id ?? actingPilotId,
      actingPilotId,
    );

    return { event };
  });

export const unpublishHostEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.id);

    const { count, error: countError } = await context.supabase
      .from("event_rsvps")
      .select("id", { count: "exact", head: true })
      .eq("event_id", data.id)
      .neq("status", "cancelled");

    if (countError) {
      console.error("unpublishHostEvent rsvp count failed", countError);
      throw new Error("Could not check RSVPs for this event.");
    }

    const { count: communityCount } = await context.supabase
      .from("community_rsvps")
      .select("id", { count: "exact", head: true })
      .eq("event_id", data.id)
      .neq("status", "cancelled");

    if (!canUnpublish(count ?? 0, communityCount ?? 0)) {
      throw new Error(UNPUBLISH_WITH_RSVPS_MESSAGE);
    }

    const { data: event, error } = await context.supabase
      .from("events")
      .update({ status: "draft" } as never)
      .eq("id", data.id)
      .eq("status", "planned")
      .select()
      .single();

    if (error || !event) {
      console.error("unpublishHostEvent failed", error);
      throw new Error("Could not unpublish event.");
    }
    return { event };
  });

export const updateHostEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => hostEventUpdateSchema.parse(data))
  .handler(async ({ context, data }) => {
    const { id, ...rawUpdates } = data;
    await assertHostsEvent(context, id);
    await assertMaySetEventType(context, rawUpdates.event_type);

    // Reassigning an event to a different host is an admin-only correction.
    if (
      rawUpdates.host_pilot_id &&
      !(await isTenantAdmin({ supabase: context.supabase, userId: context.userId! }))
    ) {
      throw new Error("Only an admin can change the host of an event.");
    }

    const updates = hostEventUpdatePatch(rawUpdates as Record<string, unknown>);

    const { data: event, error } = await context.supabase
      .from("events")
      .update(updates as never)
      .eq("id", id)
      .select()
      .single();

    if (error || !event) {
      console.error("updateHostEvent failed", error);
      if (error?.code === "23505") {
        throw new Error("That event link is already taken — try a different one.");
      }
      if (error?.code === "42501") {
        throw new Error("We couldn't confirm your sign-in. Sign in again and retry.");
      }
      throw new Error("Could not update event.");
    }

    return { event };
  });

export const cancelHostEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.id);

    const { data: event, error } = await context.supabase
      .from("events")
      .update({ status: "cancelled" })
      .eq("id", data.id)
      .select()
      .single();

    if (error || !event) {
      console.error("cancelHostEvent failed", error);
      throw new Error("Could not cancel event.");
    }
    return { event };
  });

const cohostSchema = z.object({
  eventId: z.string().uuid(),
  pilotId: z.string().uuid(),
});

export const searchCoHostPilots = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ q: z.string().trim().max(80).default("") }).parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertHost(context);
    const { data: rows, error } = await context.supabase.rpc("search_pilots_for_cohost", {
      _q: data.q,
    });
    if (error) {
      console.error("searchCoHostPilots failed", error);
      throw new Error("Could not search pilots.");
    }
    return {
      pilots: (rows ?? []) as Array<{
        id: string;
        label: string;
        callsign: string | null;
        is_host: boolean;
      }>,
    };
  });

export const listEventCoHosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const { data: rows, error } = await context.supabase.rpc("event_cohosts_for_host", {
      _event_id: data.eventId,
    });
    if (error) {
      console.error("listEventCoHosts failed", error);
      throw new Error("Could not load co-hosts.");
    }
    return {
      cohosts: (rows ?? []) as Array<{ pilot_id: string; label: string; callsign: string | null }>,
    };
  });

export const addEventCoHost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => cohostSchema.parse(data))
  .handler(async ({ context, data }) => {
    // add_event_cohost enforces owner/admin, grants host access, and is idempotent.
    const { error } = await context.supabase.rpc("add_event_cohost", {
      _event_id: data.eventId,
      _pilot_id: data.pilotId,
    });
    if (error) {
      console.error("addEventCoHost failed", error);
      throw new Error(
        error.message?.includes("owner")
          ? "Only the event owner can manage co-hosts."
          : "Could not add co-host.",
      );
    }

    // Tell the owner who joined, and tell the new co-host they have access.
    try {
      const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
      const { data: event } = await context.supabase
        .from("events")
        .select("id, title, slug, starts_at, location, host_pilot_id")
        .eq("id", data.eventId)
        .single();
      if (!event) return { ok: true };

      const cohost = await hostIdentity(context.supabase, data.pilotId);
      const owner = event.host_pilot_id
        ? await hostIdentity(context.supabase, event.host_pilot_id)
        : null;
      const label = (h: { name: string; callsign: string | null } | null) =>
        h ? (h.callsign ? `${h.name} (${h.callsign})` : h.name) : "";

      const shared = {
        ownerLabel: label(owner),
        cohostLabel: label(cohost),
        cohostEmail: cohost.email ?? "",
        eventTitle: event.title,
        eventSlug: event.slug,
        eventStartsAt: event.starts_at,
        eventLocation: event.location ?? "",
      };

      if (owner?.email) {
        await sendTemplateEmail("cohost-added", owner.email, {
          templateData: {
            ...shared,
            recipientRole: "owner",
            recipientFirstName: owner.name.split(" ")[0] ?? "",
          },
          idempotencyKey: `cohost-added-owner-${event.id}-${data.pilotId}`,
        });
      }
      if (cohost.email) {
        await sendTemplateEmail("cohost-added", cohost.email, {
          templateData: {
            ...shared,
            recipientRole: "cohost",
            recipientFirstName: cohost.name.split(" ")[0] ?? "",
          },
          idempotencyKey: `cohost-added-cohost-${event.id}-${data.pilotId}`,
        });
      }
    } catch (e) {
      console.error("co-host added email failed", e);
    }

    return { ok: true };
  });

export const removeEventCoHost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => cohostSchema.parse(data))
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase.rpc("remove_event_cohost", {
      _event_id: data.eventId,
      _pilot_id: data.pilotId,
    });
    if (error) {
      console.error("removeEventCoHost failed", error);
      throw new Error("Could not remove co-host.");
    }
    return { ok: true };
  });

/**
 * Invite a co-host who has no pilot record yet.
 *
 * The invite is pending until that email signs in — identity resolution
 * attaches them to the event automatically at that point.
 */
export const inviteCoHostByEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ eventId: z.string().uuid(), email: z.string().trim().email().max(255) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.eventId);
    const normalized = data.email.trim().toLowerCase();

    const { data: event } = await context.supabase
      .from("events")
      .select("id, title, slug, starts_at, location, host_pilot_id")
      .eq("id", data.eventId)
      .single();
    if (!event) throw new Error("Event not found.");

    // Already a pilot? Add them properly instead of leaving an invite hanging.
    const { data: existingPilot } = await context.supabase
      .from("pilots")
      .select("id")
      .eq("email_normalized", normalized)
      .is("merged_into_pilot_id", null)
      .maybeSingle();
    if (existingPilot) {
      const { error: addErr } = await context.supabase.rpc("add_event_cohost", {
        _event_id: data.eventId,
        _pilot_id: existingPilot.id,
      });
      if (addErr) {
        console.error("inviteCoHostByEmail add failed", addErr);
        throw new Error("Could not add that co-host.");
      }
      return { ok: true as const, added: true as const };
    }

    const inviter = event.host_pilot_id
      ? await hostIdentity(context.supabase, event.host_pilot_id)
      : null;

    const { error } = await context.supabase.from("event_cohost_invites").upsert(
      {
        event_id: data.eventId,
        email: data.email.trim(),
        email_normalized: normalized,
        invited_by_pilot_id: event.host_pilot_id ?? null,
        status: "pending",
      } as never,
      { onConflict: "event_id,email_normalized" },
    );
    if (error) {
      console.error("inviteCoHostByEmail failed", error);
      throw new Error("Could not send that co-host invite.");
    }

    try {
      const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
      await sendTemplateEmail("cohost-invite", data.email.trim(), {
        templateData: {
          ownerLabel: inviter
            ? inviter.callsign
              ? `${inviter.name} (${inviter.callsign})`
              : inviter.name
            : "",
          eventTitle: event.title,
          eventSlug: event.slug,
          eventStartsAt: event.starts_at,
          eventLocation: event.location ?? "",
        },
        idempotencyKey: `cohost-invite-${event.id}-${normalized}`,
      });
    } catch (e) {
      console.error("co-host invite email failed", e);
    }

    return { ok: true as const, added: false as const };
  });

export const listCoHostInvites = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const { data: rows, error } = await context.supabase
      .from("event_cohost_invites")
      .select("id, email, status, created_at, accepted_at")
      .eq("event_id", data.eventId)
      .eq("status", "pending")
      .order("created_at", { ascending: true });
    if (error) {
      console.error("listCoHostInvites failed", error);
      throw new Error("Could not load pending co-host invites.");
    }
    return {
      invites: (rows ?? []) as Array<{
        id: string;
        email: string;
        status: string;
        created_at: string;
        accepted_at: string | null;
      }>,
    };
  });

export const revokeCoHostInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ inviteId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase
      .from("event_cohost_invites")
      .update({ status: "revoked" } as never)
      .eq("id", data.inviteId);
    if (error) {
      console.error("revokeCoHostInvite failed", error);
      throw new Error("Could not revoke that invite.");
    }
    return { ok: true };
  });

/** One attendee row, whether they came in as a pilot or a community guest. */
export type HostAttendee = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
  guest_count: number;
  vtx_preference: string | null;
  freq_status: string | null;
  checked_in_at: string | null;
  responded_at: string;
  is_pilot: boolean;
};

export const getHostEventAttendees = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.eventId);

    const [community, pilotSide, eventRow] = await Promise.all([
      context.supabase
        .from("community_rsvps")
        .select(
          "id, status, guest_count, note, vtx_preference, freq_status, checked_in_at, responded_at, created_at, community_contacts!inner(first_name, last_name, email, phone, is_minor)",
        )
        .eq("event_id", data.eventId)
        .order("created_at", { ascending: true }),
      context.supabase
        .from("event_rsvps")
        .select(
          "id, status, guest_count, vtx_preference, freq_status, checked_in_at, responded_at, guest_name, guest_email, external_name, pilots!left(first_name, last_name, callsign, email, phone)",
        )
        .eq("event_id", data.eventId)
        .order("responded_at", { ascending: true }),
      context.supabase
        .from("events")
        .select("capacity, spectator_capacity")
        .eq("id", data.eventId)
        .maybeSingle(),
    ]);

    if (community.error || pilotSide.error) {
      console.error("getHostEventAttendees failed", community.error ?? pilotSide.error);
      throw new Error("Could not load attendees.");
    }

    const attendees: HostAttendee[] = [
      ...((pilotSide.data ?? []) as any[]).map((r) => ({
        id: r.id,
        name:
          (r.pilots
            ? r.pilots.callsign ||
              [r.pilots.first_name, r.pilots.last_name].filter(Boolean).join(" ")
            : "") ||
          r.guest_name ||
          r.external_name ||
          r.guest_email ||
          "Guest",
        email: r.pilots?.email ?? r.guest_email ?? null,
        phone: r.pilots?.phone ?? null,
        status: r.status,
        guest_count: r.guest_count ?? 0,
        vtx_preference: r.vtx_preference ?? null,
        freq_status: r.freq_status ?? null,
        checked_in_at: r.checked_in_at ?? null,
        responded_at: r.responded_at ?? r.created_at,
        is_pilot: Boolean(r.pilots),
      })),
      ...((community.data ?? []) as any[]).map((r) => ({
        id: r.id,
        name:
          [r.community_contacts?.first_name, r.community_contacts?.last_name]
            .filter(Boolean)
            .join(" ") ||
          r.community_contacts?.email ||
          "Guest",
        email: r.community_contacts?.email ?? null,
        phone: r.community_contacts?.phone ?? null,
        status: r.status,
        guest_count: r.guest_count ?? 0,
        vtx_preference: r.vtx_preference ?? null,
        freq_status: r.freq_status ?? null,
        checked_in_at: r.checked_in_at ?? null,
        responded_at: r.responded_at ?? r.created_at,
        is_pilot: false,
      })),
    ].sort((a, b) => a.responded_at.localeCompare(b.responded_at));

    const going = attendees.filter((a) => a.status === "going" || a.status === "attended");
    const counts = {
      going: going.length,
      waitlisted: attendees.filter((a) => a.status === "waitlisted").length,
      guests: going.reduce((sum, a) => sum + (a.guest_count ?? 0), 0),
      checkedIn: attendees.filter((a) => a.checked_in_at).length,
      capacity: (eventRow.data as any)?.capacity ?? null,
      spectatorCapacity: (eventRow.data as any)?.spectator_capacity ?? null,
    };

    return { attendees, counts };
  });

/** Tokenized, revocable subscribe feed for the signed-in host's own calendar. */
export const getHostCalendarToken = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertHost(context);
    const pilotId = await pilotIdForUser(context.supabase, context.userId!);
    const { data, error } = await context.supabase
      .from("pilots")
      .select("host_calendar_token")
      .eq("id", pilotId)
      .maybeSingle();
    if (error || !data) throw new Error("Could not load your calendar feed.");
    return { token: (data as any).host_calendar_token as string };
  });

const inviteSchema = z.object({
  eventId: z.string().uuid(),
  emails: z.string().trim().min(1),
});

export const inviteToHostEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => inviteSchema.parse(data))
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.eventId);

    const { data: event, error: eventError } = await context.supabase
      .from("events")
      .select("title, slug, starts_at, location")
      .eq("id", data.eventId)
      .single();

    if (eventError || !event) throw new Error("Event not found.");

    const raw = data.emails.split(/[,;\n]+/).map((s) => s.trim());
    const seen = new Set<string>();
    const emails: string[] = [];
    const invalid: string[] = [];
    for (const e of raw) {
      if (!e) continue;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
        invalid.push(e);
        continue;
      }
      const lower = e.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      emails.push(lower);
    }

    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    const sent: string[] = [];
    for (const email of emails) {
      const result = await sendTemplateEmail("host-event-invite", email, {
        templateData: {
          eventTitle: event.title,
          eventSlug: event.slug,
          eventStartsAt: event.starts_at,
          eventLocation: event.location ?? "",
        },
        idempotencyKey: `host-invite-${data.eventId}-${email}`,
      });
      if (result.sent) sent.push(email);
    }

    return { sent, invalid };
  });

export const listCommunityEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({})
      .optional()
      .parse(data ?? {}),
  )
  .handler(async ({ context }) => {
    if (!(await isTenantAdmin(context))) {
      throw new Error("Admin access required.");
    }
    const { data, error } = await context.supabase
      .from("events")
      .select(
        "id, title, slug, description, location, starts_at, ends_at, cover_image_url, status, campaign_id, partiful_url, multigp_event_url, rsvp_mode, featured, capacity, spectator_capacity, host_kind, host_pilot_id, freq_enabled, event_type, created_at, updated_at, pilots!left(first_name, last_name, callsign)",
      )
      .eq("host_kind", "community")
      .order("starts_at", { ascending: true });

    if (error) {
      console.error("listCommunityEvents failed", error);
      throw new Error("Could not load community events.");
    }

    const events = (data ?? []).map((row: any) => ({
      ...row,
      host_name: row.pilots
        ? [row.pilots.first_name, row.pilots.last_name].filter(Boolean).join(" ") ||
          row.pilots.callsign ||
          "Unknown"
        : "Unknown",
    }));
    return { events };
  });

/**
 * "Can I manage this event?" — asked by the public event page so hosts,
 * co-hosts and admins get an inline toolbar instead of hunting for /host.
 * Returns `canManage: false` rather than throwing for ordinary pilots.
 */
export const getEventManageContext = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ slug: z.string().trim().min(1).max(120) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const empty = {
      canManage: false,
      isAdmin: false,
      isCrew: false,
      eventId: null as string | null,
      status: null as string | null,
      freqEnabled: false,
    };

    const { data: row } = await context.supabase
      .from("events")
      .select("id, status, freq_enabled")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!row) return empty;

    const event = row as { id: string; status: string; freq_enabled: boolean | null };
    const admin = await isTenantAdmin(context as never);

    let hosts = false;
    let crew = false;
    if (!admin) {
      const { data: h } = await context.supabase.rpc("hosts_event", {
        _event_id: event.id,
      } as never);
      hosts = !!h;

      const { uid, tenantId } = await dbIdentity(context as never);
      const { data: c } = await context.supabase.rpc("is_crew", {
        _user_id: uid,
        _tenant_id: tenantId,
      } as never);
      crew = !!c;
    }

    return {
      canManage: admin || crew || hosts,
      isAdmin: admin,
      isCrew: admin || crew,
      eventId: event.id,
      status: event.status,
      freqEnabled: !!event.freq_enabled,
    };
  });

/** Host-managed status change for any attendee on their event. */
export const setHostRsvpStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        eventId: z.string().uuid(),
        rsvpId: z.string().uuid(),
        isPilot: z.boolean(),
        status: z.enum(["going", "maybe", "cancelled", "waitlisted", "no_show"]),
        changeNote: z.string().trim().max(500).optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.eventId);
    if (data.isPilot) {
      const { setRsvpStatus } = await import("@/lib/rsvps.functions");
      return setRsvpStatus({
        data: { rsvpId: data.rsvpId, status: data.status as never, changeNote: data.changeNote },
      });
    }
    // Community RSVP: simple status update with capacity-aware waitlist handling.
    const { data: row } = await context.supabase
      .from("community_rsvps")
      .select("id, status, guest_count, event_id")
      .eq("id", data.rsvpId)
      .eq("event_id", data.eventId)
      .maybeSingle();
    if (!row) throw new Error("Could not find that RSVP.");

    let newStatus = data.status;
    const guestCount = row.guest_count ?? 0;
    if (data.status === "going") {
      const { data: event } = await context.supabase
        .from("events")
        .select("capacity, spectator_capacity")
        .eq("id", data.eventId)
        .maybeSingle();
      const capacity = (event as { capacity: number } | null)?.capacity ?? 999;
      const { count: goingCount } = await context.supabase
        .from("community_rsvps")
        .select("id", { count: "exact", head: true })
        .eq("event_id", data.eventId)
        .in("status", ["going", "attended"]);
      if ((goingCount ?? 0) + 1 + guestCount > capacity) {
        newStatus = "waitlisted";
      }
    }

    const update: Record<string, unknown> = {
      status: newStatus,
      guest_count: guestCount,
      responded_at: new Date().toISOString(),
    };
    if (data.changeNote) update["note"] = data.changeNote;

    const { error } = await context.supabase
      .from("community_rsvps")
      .update(update as never)
      .eq("id", data.rsvpId);
    if (error) throw new Error(error.message);
    return { ok: true, status: newStatus };
  });

/** Host-managed detail update for any attendee on their event. */
export const updateHostRsvpDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        eventId: z.string().uuid(),
        rsvpId: z.string().uuid(),
        isPilot: z.boolean(),
        guestCount: z.number().int().min(0).max(6).optional(),
        note: z.string().trim().max(500).nullable().optional(),
        changeNote: z.string().trim().max(500).optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertHostsEvent(context, data.eventId);
    if (data.isPilot) {
      const { updateRsvpDetails } = await import("@/lib/rsvps.functions");
      return updateRsvpDetails({
        data: {
          rsvpId: data.rsvpId,
          guestCount: data.guestCount,
          note: data.note,
          changeNote: data.changeNote,
        },
      });
    }
    const update: Record<string, unknown> = {};
    if (data.guestCount !== undefined) update["guest_count"] = data.guestCount;
    if (data.note !== undefined) update["note"] = data.note;
    if (data.changeNote) update["note"] = data.changeNote;
    if (Object.keys(update).length === 0) return { ok: true };
    const { error } = await context.supabase
      .from("community_rsvps")
      .update(update as never)
      .eq("id", data.rsvpId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
